import { Flex } from 'antd'
import { EKPIUnit } from 'enums/kpi'
import { Bar, BarChart, CartesianGrid, LegendProps, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import NumberUtil from 'utils/number'
import BaseTitle from '../BaseTitle'
import { useTooltipContext } from '../BaseTooltip/CustomTooltip'
import { PERIOD_TYPE_TRANSLATE, TRANSLATE } from 'constants/translate'

interface IProgressChartRes {
  name: string
  value: number
  value1: number
  comments: string
}
export interface RechartsProps {
  key: string
  yAxisId?: string
  color: string
  display: boolean
  isHover: boolean
}

interface BaseTinyBarChartProps<T> {
  data: IProgressChartRes[]
  colors: any
  chartProps?: Array<RechartsProps>
  yAxisProps?: Array<{
    yAxisId: string
    orientation: 'left' | 'right'
    unit: string
  }>
  width?: string | number
  height?: string | number
  margin?: {
    top?: number
    bottom?: number
    right?: number
    left?: number
  }
  legendProps?: Pick<
    LegendProps,
    'height' | 'width' | 'layout' | 'align' | 'margin' | 'formatter' | 'iconType' | 'iconSize' | 'onClick'
  >
}

const formatData = (data: any) => {
  if (data?.milestones) {
    const dataFormat = data.milestones.map((milestone: any) => {
      const milestones: any = []
      milestone.data.forEach((data: any) => {
        if (data.name !== 'Grand total') {
          milestones.push({
            ...data,
            name: `${TRANSLATE.milestone} ${data?.name.slice(1)}`,
            value: data.value,
            value1: 100 - data.value,
            comments: milestone?.comments,
            original: {
              grandTotal: milestone?.grandTotal,
              progressTotal: milestone?.progressTotal
            }
          })
        }
      })

      return milestones
    })

    return dataFormat
  }
  return []
}

function BaseTinyBarChart<T>({ data, colors, width = '100%', height = '100%', ...props }: BaseTinyBarChartProps<T>) {
  const dataChart = formatData(data)

  const { openTooltip, closeTooltip } = useTooltipContext()

  const tooltipContent = (e: any) => {
    return <span>{e && NumberUtil.format(e.toFixed(2), { unit: EKPIUnit.PERCENT })}</span>
  }

  return (
    <div className='tinybar-chart overflow-y-auto h-full flex flex-col gap-6'>
      {dataChart.map((d: IProgressChartRes[], i: number) => {
        return (
          <Flex
            key={i}
            className={`h-full gap-8 justify-between`}
            style={{
              minHeight: (d.length + 2) * 50 + 'px',
              maxHeight: (d.length + 2) * 50 + 'px'
            }}
          >
            <div className='w-2/3 flex flex-col'>
              <ResponsiveContainer width={'100%'} height={'100%'} className={'grow-1 h-full'}>
                <BarChart data={d} {...props} layout='vertical' barSize={32} margin={{ left: 10 }}>
                  <CartesianGrid stroke='#FFFF' />
                  <XAxis type='number' />
                  <YAxis dataKey='name' type='category' width={100} />
                  <Bar
                    dataKey='value'
                    stackId='a'
                    fill={colors?.[0]}
                    onMouseEnter={(e) => {
                      const value = e.tooltipPayload[0].value
                      openTooltip({
                        content: tooltipContent(parseFloat(value))
                      })
                    }}
                    onMouseLeave={() => closeTooltip()}
                  />
                  <Bar
                    dataKey='value1'
                    stackId='a'
                    fill={colors?.[1]}
                    onMouseEnter={(e) => {
                      const value = e.tooltipPayload[0].value
                      openTooltip({
                        content: tooltipContent(parseFloat(value))
                      })
                    }}
                    onMouseLeave={() => closeTooltip()}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className='w-1/3 flex flex-col'>
              <BaseTitle title={'Comments/ Remarks'} className='mb-4' />
              <div className='border rounded-md h-full grow-1 border-medium p-4 overflow-y-auto'>{d[0]?.comments}</div>
            </div>
          </Flex>
        )
      })}
    </div>
  )
}

export default BaseTinyBarChart
